<script setup>
import { ref, computed, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useHighlightNewRow } from '@/composables/scroll'
import { useMyTaskFilter, useTaskActions, useTaskLoader, useTaskServiceOptions } from '@/composables/tasks'

import DockablePanel from '@/components/shared/dockable-panel/DockablePanel.vue'
import DockablePanelHeader from '@/components/shared/dockable-panel/DockablePanelHeader.vue'
import TaskEdit from '@/components/task/TaskEdit.vue'
import TaskGroups from '@/components/task/TaskGroups.vue'

const i18n = getI18n()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()
const { ensureServicesAsync } = useTaskServiceOptions()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const editTask = ref(null)
const body = ref(null)

const { tasks, loadAsync } = useTaskLoader(() => scheduleStore.fetchTasksAsync({ active: true }), {
  onNewTaskAsync: (taskId) => highlightRowAsync(taskId, body),
  enabled: () => webSiteStore.showTasks,
})

const isMine = useMyTaskFilter()
const visibleTasks = computed(() => (webSiteStore.taskPanelShowMine ? tasks.value.filter(isMine) : tasks.value))
const openCount = computed(() => visibleTasks.value.filter(task => !task.completed).length)
const isEmpty = computed(() => !visibleTasks.value.length)

const { toggleCompletedAsync, newTaskDefaults } = useTaskActions()

watch(() => webSiteStore.showTasks, async(show) => {
  if (show) await Promise.all([scheduleStore.ensureTaskTypesAsync(), ensureServicesAsync(), loadAsync(false)])
}, { immediate: true })
</script>

<template>
  <dockable-panel
    v-model="webSiteStore.showTasks"
    v-model:width="webSiteStore.taskPanelWidth"
    v-model:bounds="webSiteStore.taskWindowBounds"
    :docked="webSiteStore.taskPanelDocked"
  >
    <template #default="{ floating, move, close }">
      <div class="column no-wrap full-height">
        <dockable-panel-header
          v-model:docked="webSiteStore.taskPanelDocked"
          icon="mdi-checkbox-marked-circle-outline"
          :title="i18n.t('common.tasks.tab')"
          :caption="i18n.t('common.tasks.openCount', { count: openCount })"
          :floating="floating"
          @move="move"
          @close="close"
        >
          <template #actions>
            <q-btn
              flat
              round
              dense
              icon="mdi-account-check-outline"
              :color="webSiteStore.taskPanelShowMine ? 'primary' : undefined"
              @click="webSiteStore.taskPanelShowMine = !webSiteStore.taskPanelShowMine"
            >
              <q-tooltip>{{ i18n.t('common.tasks.showMine') }}</q-tooltip>
            </q-btn>
            <q-btn
              flat
              round
              dense
              icon="mdi-shape-outline"
              :color="webSiteStore.taskPanelGroupByType ? 'primary' : undefined"
              @click="webSiteStore.taskPanelGroupByType = !webSiteStore.taskPanelGroupByType"
            >
              <q-tooltip>{{ i18n.t('common.tasks.splitByType') }}</q-tooltip>
            </q-btn>
            <q-btn
              flat
              round
              dense
              icon="mdi-check-all"
              :color="webSiteStore.taskPanelShowCompleted ? 'primary' : undefined"
              @click="webSiteStore.taskPanelShowCompleted = !webSiteStore.taskPanelShowCompleted"
            >
              <q-tooltip>{{ i18n.t('common.tasks.showCompleted') }}</q-tooltip>
            </q-btn>
            <q-btn
              flat
              round
              dense
              icon="mdi-plus"
              @click="editTask = newTaskDefaults()"
            >
              <q-tooltip>{{ i18n.t('common.tasks.newTask') }}</q-tooltip>
            </q-btn>
          </template>
        </dockable-panel-header>
        <q-scroll-area class="col">
          <div ref="body" class="q-pa-sm">
            <task-groups
              v-if="!isEmpty"
              v-model:collapsed="webSiteStore.taskPanelCollapsedGroups"
              :tasks="visibleTasks"
              :split-by-type="webSiteStore.taskPanelGroupByType"
              :show-completed="webSiteStore.taskPanelShowCompleted"
              :highlighted-id="highlightedRowId"
              @toggle="toggleCompletedAsync"
              @open="editTask = $event"
            />
            <div v-else class="column flex-center q-pa-lg text-center text-grey-7">
              <q-icon
                name="mdi-checkbox-marked-circle-outline"
                color="primary"
                size="xl"
                class="q-mb-md"
              />
              <div>{{ i18n.t('common.tasks.noTasks') }}</div>
            </div>
          </div>
        </q-scroll-area>
      </div>
    </template>
  </dockable-panel>

  <q-dialog :model-value="editTask !== null" class="dialog-xs" @hide="editTask = null">
    <task-edit
      v-if="editTask !== null"
      :model-value="editTask"
      project-selectable
      @update="editTask = null"
      @cancel="editTask = null"
    />
  </q-dialog>
</template>
